// ============================================
// Material Reflection — from Slang's reflection JSON
// ============================================
//
// The Slang counterpart of `reflectWgsl` (MaterialReflection.ts). Where the WGSL path scrapes `//!` comments
// + struct text with regex, the Slang path consumes slangc's `-reflection-json` (also available in-editor via
// slang-wasm `getLayout().toJsonObject()`) — a real, typed reflection. It produces the SAME
// `MaterialReflection` shape, so every downstream consumer (MaterialAsset.loadMaterialAsset,
// shader.assetInspector, the material importer) is unchanged: reflection is a SOURCE-SWAP.
//
// Authoring surface (Slang user-defined attributes, declared in `engine.material` / a prelude):
//   struct Uniforms {
//     float4x4 viewProjection;                 // engine-fed (skipped)
//     [Color] float4 tint;                     // → property type 'color'
//     [Range(0,10)] [Label("Speed")] float speed;
//   }
//   [vk::binding(2,0)] Texture2D albedo;       // → texture slot ALBEDO (in binding order)
//   [vk::constant_id(0)] const bool USE_GLOW = false;   // → override (variant)
//   [Supports("sprite")] [Tag("transparent")] [Requires("lighting")]
//   [shader("fragment")] float4 fsMain(...) { ... }     // decl-level material attributes
//
// A `.slang` with no `[Supports(...)]` attribute is a plain shader (not material-assignable) → returns null,
// mirroring reflectWgsl's "no `//! supports:`" rule.

import { type TextureSlotName, type MaterialTag } from '../types';
import type {
  MaterialReflection,
  MaterialProperty,
  MaterialPropertyType,
  MaterialOverride,
  MaterialOverrideType,
  MaterialTextureSlotDef,
} from './MaterialReflection';

const TEXTURE_SLOTS: readonly TextureSlotName[] = ['ALBEDO', 'NORMAL', 'EMISSION', 'MASK'];

// ---- Slang reflection JSON shapes (the subset we read; slangc `-reflection-json`) -------------------

interface SlangAttr {
  name: string;
  arguments?: Array<number | string>;
}
interface SlangType {
  kind: string; // constantBuffer | struct | vector | scalar | matrix | resource | samplerState | ...
  scalarType?: string; // float32 | int32 | uint32 | bool | ...
  elementCount?: number; // vector
  rowCount?: number; // matrix
  columnCount?: number;
  elementType?: SlangType;
  baseShape?: string; // resource: texture2D | ...
  fields?: SlangField[]; // struct
  name?: string;
}
interface SlangBinding {
  kind: string; // descriptorTableSlot | uniform | specializationConstant | varyingInput | ...
  index?: number;
}
interface SlangField {
  name: string;
  type: SlangType;
  userAttribs?: SlangAttr[];
  binding?: SlangBinding;
}
interface SlangParam {
  name: string;
  binding?: SlangBinding;
  type?: SlangType;
  userAttribs?: SlangAttr[];
}
interface SlangEntryPoint {
  name: string;
  stage?: string;
  userAttribs?: SlangAttr[];
}
export interface SlangReflectionJson {
  parameters?: SlangParam[];
  entryPoints?: SlangEntryPoint[];
}

// ---- Helpers --------------------------------------------------------------------------------------

/** Find the first attribute (case-insensitive) on a list, or undefined. */
function attr(attrs: SlangAttr[] | undefined, name: string): SlangAttr | undefined {
  return attrs?.find((a) => a.name.toLowerCase() === name.toLowerCase());
}

/** All string arguments of every attribute named `name`, comma-split + flattened (so Supports("a,b") and
 * repeated [Supports("a")][Supports("b")] both work). */
function collectStringArgs(attrLists: Array<SlangAttr[] | undefined>, name: string): string[] {
  const out: string[] = [];
  for (const attrs of attrLists) {
    for (const a of attrs ?? []) {
      if (a.name.toLowerCase() !== name.toLowerCase()) continue;
      for (const arg of a.arguments ?? []) {
        if (typeof arg === 'string') out.push(...arg.split(',').map((s) => s.trim()).filter(Boolean));
      }
    }
  }
  return out;
}

/** Map a Slang field type → an authoring property type (or null if unsupported, e.g. matrix/u32). */
function slangTypeToPropertyType(t: SlangType, isColor: boolean): MaterialPropertyType | null {
  if (t.kind === 'vector' && t.elementType?.scalarType === 'float32') {
    if (t.elementCount === 4) return isColor ? 'color' : 'vec4';
    if (t.elementCount === 3) return 'vec3';
    if (t.elementCount === 2) return 'vec2';
    return null;
  }
  if (t.kind === 'scalar') {
    if (t.scalarType === 'float32') return 'f32';
    if (t.scalarType === 'int32') return 'i32';
  }
  return null; // matrix (viewProjection), uint32, bool, etc. — not a material property
}

function slangScalarToOverrideType(scalarType: string | undefined): MaterialOverrideType | null {
  switch (scalarType) {
    case 'bool':
      return 'bool';
    case 'float32':
      return 'f32';
    case 'int32':
      return 'i32';
    case 'uint32':
      return 'u32';
    default:
      return null;
  }
}

/** Find the material uniform struct: the lowest-index constantBuffer parameter's element fields. */
function findUniformFields(params: SlangParam[]): SlangField[] | null {
  const cbs = params
    .filter((p) => p.type?.kind === 'constantBuffer')
    .sort((a, b) => (a.binding?.index ?? 0) - (b.binding?.index ?? 0));
  for (const cb of cbs) {
    const fields = cb.type?.elementType?.fields;
    if (fields && fields.length) return fields;
  }
  return null;
}

/** Parse spec-constant defaults out of the emitted WGSL, keyed by `@id(N)` (Slang mangles the name but the
 * id is stable). Returns id → default (number|boolean). */
function parseOverrideDefaults(wgsl: string): Map<number, number | boolean> {
  const out = new Map<number, number | boolean>();
  if (!wgsl) return out;
  // e.g.  @id(0) override USE_GLOW_0 : bool = false;   /   @id(1) override N_0 : u32 = u32(16);
  const re = /@id\((\d+)\)\s+override\s+\w+\s*:\s*\w+\s*=\s*([^;]+);/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(wgsl)) !== null) {
    const id = Number(m[1]);
    let raw = m[2].trim();
    // unwrap conversions like `u32(16)` / `i32(3)` / `f32(1.0)`
    const conv = /^[a-z]\d*\(([^)]*)\)$/.exec(raw);
    if (conv) raw = conv[1].trim();
    let val: number | boolean;
    if (raw === 'true') val = true;
    else if (raw === 'false') val = false;
    else {
      val = Number(raw);
      if (Number.isNaN(val)) continue;
    }
    out.set(id, val);
  }
  return out;
}

// ---- Public API -----------------------------------------------------------------------------------

/**
 * Reflect a material's surface from Slang's reflection JSON (slangc `-reflection-json` /
 * slang-wasm `getLayout().toJsonObject()`). Optionally pass the emitted WGSL to recover override defaults.
 * Returns null if the shader has no `[Supports(...)]` attribute (= a plain, non-material shader).
 * Never throws — a malformed reflection yields a partial result.
 */
export function reflectSlang(reflection: unknown, emittedWgsl = ''): MaterialReflection | null {
  const json = (reflection ?? {}) as SlangReflectionJson;
  const params = json.parameters ?? [];
  const entryPoints = json.entryPoints ?? [];

  // Decl-level attributes live on the entry points (any stage) — merge across them.
  const epAttrs = entryPoints.map((e) => e.userAttribs);
  const supports = collectStringArgs(epAttrs, 'Supports');
  if (supports.length === 0) return null; // not a material

  const tagArgs = collectStringArgs(epAttrs, 'Tag');
  const tag = tagArgs.length ? (tagArgs[0].toLowerCase() as MaterialTag) : undefined;
  const requires = collectStringArgs(epAttrs, 'Requires').map((s) => s.toLowerCase());

  // Properties — the material uniform struct's fields (skip engine-fed viewProjection + time).
  const properties: MaterialProperty[] = [];
  let structTime = false;
  for (const f of findUniformFields(params) ?? []) {
    if (/^u?viewProjection$/i.test(f.name)) continue;
    if (/^u?time$/i.test(f.name)) {
      structTime = true;
      continue;
    }
    const isColor = !!attr(f.userAttribs, 'Color');
    const type = slangTypeToPropertyType(f.type, isColor);
    if (!type) continue; // unsupported uniform field type

    const prop: MaterialProperty = { name: f.name, type };
    const range = attr(f.userAttribs, 'Range');
    if (range?.arguments && range.arguments.length >= 2) {
      prop.min = Number(range.arguments[0]);
      prop.max = Number(range.arguments[1]);
    }
    const label = attr(f.userAttribs, 'Label');
    if (typeof label?.arguments?.[0] === 'string') prop.label = label.arguments[0];
    const tooltip = attr(f.userAttribs, 'Tooltip');
    if (typeof tooltip?.arguments?.[0] === 'string') prop.tooltip = tooltip.arguments[0];
    const group = attr(f.userAttribs, 'Group');
    if (typeof group?.arguments?.[0] === 'string') prop.group = group.arguments[0];
    const step = attr(f.userAttribs, 'Step');
    if (typeof step?.arguments?.[0] === 'number') prop.step = step.arguments[0];
    const def = attr(f.userAttribs, 'Default');
    if (def?.arguments && def.arguments.length) {
      const nums = def.arguments.filter((a): a is number => typeof a === 'number');
      if (nums.length === 1) prop.default = nums[0];
      else if (nums.length > 1) prop.default = nums;
    }
    properties.push(prop);
  }

  // Textures — resource (texture2D) parameters, in binding order → ALBEDO/NORMAL/EMISSION/MASK.
  const texParams = params
    .filter((p) => p.type?.kind === 'resource' && p.type.baseShape === 'texture2D')
    .sort((a, b) => (a.binding?.index ?? 0) - (b.binding?.index ?? 0));
  const textures: MaterialTextureSlotDef[] = [];
  texParams.forEach((p, i) => {
    if (i < TEXTURE_SLOTS.length) textures.push({ slot: TEXTURE_SLOTS[i], label: p.name });
  });

  // Overrides — specialization-constant parameters (name is unmangled in the JSON; default from WGSL by @id).
  const overrideDefaults = parseOverrideDefaults(emittedWgsl);
  const overrides: MaterialOverride[] = [];
  for (const p of params) {
    if (p.binding?.kind !== 'specializationConstant') continue;
    const type = slangScalarToOverrideType(p.type?.scalarType);
    if (!type) continue;
    const id = p.binding.index;
    overrides.push({ name: p.name, type, id, default: id !== undefined ? overrideDefaults.get(id) : undefined });
  }

  return {
    supports,
    tag,
    properties,
    overrides,
    textures,
    requires,
    usesTime: requires.includes('time') || structTime,
    usesLighting: requires.includes('lighting'),
  };
}
